import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  TextField,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Alert,
  FormGroup,
  Checkbox,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';

const TOKEN = 'rpa_35C9MEK57F63AUG7S6DIHJJ876T0SSA16JPXI783dc8gf3'

const initial_questions = [ 
  {
      "key": "repair_attempts",
      "type": "multiple_choice",
      "options": [
        "Never",
        "1 Time",
        "2 Times",
        "3 Times",
        "3+ Times"
      ],
      "question": "How many times have you taken your vehicle into service to resolve problems?"
  },
  {
      "key": "repair_situation",
      "type": "multiple_choice",
      "options": [
        "Unresolved - Repairs have been unsuccessful at resolving persistent issues.",
        "Mixed - Some repairs have been successful but other issues linger.",
        "Resolved - Repairs fully resolved vehicle issues.",
      ],
      "question": "Which of the following best describes your situation?"
  },
  {
      "key": "repair_nature",
      "type": "multi_select",
      "options": [
        "Steering",
        "Engine / transmission issues",
        "Electrical defects",
        "Brake malfunctions",
        "Fuel system problems",
        "Water leaks",
        "Safety concerns",
        "Cosmetic concerns",
        "I don't know"
      ],
      "question": "Which of the following best describes your issues?"
  },
  {
      "key": "model_year",
      "type": "multiple_choice",
      "options": [
        2025,
        2024,
        2023,
        2022,
        2021,
        "Older"
      ],
      "question": "What model year is your car?"
  },
  {
      "key": "make_model",
      "type": "text",
      "question": "What is vehicle make and model? (e.g. Toyota Corolla)"
  },
  {
      "key": "new_or_used",
      "type": "multiple_choice",
      "options": [
        "New",
        "Used - Not Cerified Preowned (NON CPO)",
        "Used - Certified Preowned (CPO)",
        "I Do Not Know"
      ],
      "question": "Did you get your case new or used?"
  },
  {
      "key": "state_purchased",
      "type": "multiple_choice",
      "options": [
        "California",
        "Other"
      ],
      "question": "What state was the car purchased in?"
  },
]

const Interview = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState(initial_questions);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentAnswer, setCurrentAnswer] = useState('');

  const [enoughInfo, setEnoughInfo] = useState(false);
  const [isLemonCar, setIsLemonCar] = useState(null);
  const [lemonCarExplanation, setLemonCarExplanation] = useState('');

  useEffect(() => {
    wakeUpEndPoint()
  }, []);

  useEffect(() => {
    // Reset current answer when question changes
    if (currentQuestion?.type === 'boolean' || currentQuestion?.type === 'multiple_choice') {
      setCurrentAnswer('');
    }
    else if (currentQuestion?.type == 'multi_select')
      setCurrentAnswer([]);
  }, [currentQuestionIndex]);

  const wait_for_return_if_needed = async (runpod_output, endpoint, token) => {
         var output = runpod_output
         var status = output['status']
         while (['IN_PROGRESS', 'IN_QUEUE'].includes(status)) {
             console.log("WAITING FOR JOB TO COMPLETE")
             const r = await axios.get(endpoint.replace("runsync", "status") + "/" + output['id'], { headers: { "Authorization": token }})
             console.log(r.data)
             output = r.data
             if (!output['status'])
                 return { 'status': 'timeout' }
             await new Promise(resolve => setTimeout(resolve, 1000));
             status = output['status']

         }
         return output
    }

  const prepareResponses = () => {
    return questions.map((q) => { 
      return {
        ...q, answer: answers[q.key] 
      }
    })
  }

  const wakeUpEndPoint = async () => {
    const url = 'https://api.runpod.ai/v2/rys9l573458j3n/runsync'
    const headers = {
        'Authorization': TOKEN,
        "accept": "application/json",
        "content-type": "application/json",
    }
    const response = await axios.post(url, { 
      "input": { 
        "function": "GenerateQuestion", 
        "responses": prepareResponses() 
      } 
    }, { headers });
  }

  const fetchQuestions = async () => {
    try {
      const url = 'https://api.runpod.ai/v2/rys9l573458j3n/runsync'
      const headers = {
          'Authorization': TOKEN,
          "accept": "application/json",
          "content-type": "application/json",
      }
      const response = await axios.post(url, { 
        "input": { 
          "function": "GenerateQuestion", 
          "responses": prepareResponses() 
        } 
      }, { headers });
      const output = await wait_for_return_if_needed(response.data, url, TOKEN)
      const newQuestions = JSON.parse(output.output.respond).next_question 
      const unseenQuestions = newQuestions.filter((question) => questions.filter((q) => q.key == question.key).length == 0)
      if (unseenQuestions.length > 0)
        setQuestions([...questions, ...unseenQuestions]);
      else {
        setQuestions([...questions, 
          { 
            key: "other",
            question: "Any other details you would like to share?"
          }])
      }
      setLoading(false);
    } catch (err) {
      setError('Failed to load questions. Please try again later.');
      setLoading(false);
    }
  };
  
  const evaluateCase = async () => {
    try {
      const url = 'https://api.runpod.ai/v2/rys9l573458j3n/runsync'
      const headers = {
          'Authorization': TOKEN,
          "accept": "application/json",
          "content-type": "application/json",
      }
      const response = await axios.post(url, { 
        "input": { 
          "function": "EvaluateCase", 
          "responses": prepareResponses() 
        } 
      }, { headers });
      const output = await wait_for_return_if_needed(response.data, url, TOKEN)
      const eval_info = JSON.parse(output.output.respond)
      const enough_info = eval_info.enough_information_to_determine
      if (!enough_info) {
        setEnoughInfo(false)
        fetchQuestions()
        return 
      }
      setLoading(false) 
      const is_lemon_car = eval_info.determination.is_lemon_car
      const explanation = eval_info.determination.explanation
      setIsLemonCar(is_lemon_car)
      setLemonCarExplanation(explanation)
    } catch (err) {
      setError('Failed to load questions. Please try again later.');
    }
  };

  const currentQuestion = questions[currentQuestionIndex];

  const handleNext = async () => {
    const updatedAnswers = {
      ...answers,
      [questions[currentQuestionIndex].key]: currentAnswer,
    };
    setAnswers(updatedAnswers);

    setCurrentQuestionIndex(currentQuestionIndex + 1);
    setCurrentAnswer('');
    
    if (currentQuestionIndex >= questions.length - 1) {
      setLoading(true)
      evaluateCase()
    }
  };

  if (loading) {
    return (
     <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
        <Box sx={{marginLeft: "10px"}} >
          Evaluating your case...
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  // Show result if we have a determination
  if (isLemonCar !== null) {
    return (
      <Container maxWidth="md">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            py: 4,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
            }}
          >
            {isLemonCar ? (
              <>
                <CheckCircleIcon
                  color="success"
                  sx={{ fontSize: 64 }}
                />
                <Alert 
                  severity="success" 
                  sx={{ 
                    width: '100%',
                    '& .MuiAlert-message': {
                      fontSize: '1.2rem',
                      fontWeight: 500,
                    }
                  }}
                >
                  This vehicle qualifies as a lemon car
                </Alert>
              </>
            ) : (
              <>
                <InfoIcon
                  color="action"
                  sx={{ fontSize: 64 }}
                />
                <Alert 
                  severity="info" 
                  sx={{ 
                    width: '100%',
                    bgcolor: 'grey.100',
                    '& .MuiAlert-message': {
                      fontSize: '1.2rem',
                      fontWeight: 500,
                    }
                  }}
                >
                  This vehicle does not qualify as a lemon car
                </Alert>
              </>
            )}
            
            <Typography 
              variant="body1" 
              sx={{ 
                mt: 2,
                textAlign: 'left',
                width: '100%',
                bgcolor: 'background.paper',
                p: 2,
                borderRadius: 1,
                border: 1,
                borderColor: 'divider'
              }}
            >
              {lemonCarExplanation}
            </Typography>

            <Button
              variant="outlined"
              onClick={() => {
                // Reset all states to start over
                setQuestions(initial_questions);
                setCurrentQuestionIndex(0);
                setAnswers({});
                setCurrentAnswer('');
                setEnoughInfo(false);
                setIsLemonCar(null);
                setLemonCarExplanation('');
                fetchQuestions();
              }}
              sx={{ mt: 3 }}
            >
              Start New Evaluation
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  const renderAnswerInput = () => {
    if (currentQuestion?.type === 'boolean') {
      return (
        <FormControl component="fieldset" sx={{ width: '100%', my: 3 }}>
          <RadioGroup
            row
            value={currentAnswer}
            onChange={(e) => setCurrentAnswer(e.target.value)}
            sx={{
              justifyContent: 'center',
              gap: 4
            }}
          >
            <FormControlLabel
              value="true"
              control={<Radio />}
              label="Yes"
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 2,
                mr: 0
              }}
            />
            <FormControlLabel
              value="false"
              control={<Radio />}
              label="No"
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 2,
                mr: 0
              }}
            />
          </RadioGroup>
        </FormControl>
      );
    }

    if (currentQuestion?.type === 'multi_select' && currentQuestion?.options) {
      return (
        <FormControl component="fieldset" sx={{ width: '100%', my: 3 }}>
          <FormGroup
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          > 
            {currentQuestion.options.map((option, index) => (
              <FormControlLabel
                key={index}
                value={option}
                control={
                  <Checkbox
                    checked={currentAnswer.includes(option)}
                    onChange={(e) => setCurrentAnswer((current) => [...current, e.target.value])}
                  />
                }
                label={option}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  py: 1,
                  px: 2,
                  mx: 0,
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              />
            ))}
          </FormGroup>
        </FormControl>
      );
    }
    if (currentQuestion?.type === 'multiple_choice' && currentQuestion?.options) {
      return (
        <FormControl component="fieldset" sx={{ width: '100%', my: 3 }}>
          <RadioGroup
            value={currentAnswer}
            onChange={(e) => setCurrentAnswer(e.target.value)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            {currentQuestion.options.map((option, index) => (
              <FormControlLabel
                key={index}
                value={option}
                control={<Radio />}
                label={option}
                sx={{ 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  py: 1,
                  px: 2,
                  mx: 0,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  }
                }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      );
    }

    return (
      <TextField
        fullWidth
        variant="outlined"
        value={currentAnswer}
        onChange={(e) => setCurrentAnswer(e.target.value)}
        placeholder="Type your answer here..."
        sx={{ my: 3 }}
      />
    );
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Stepper
          activeStep={currentQuestionIndex}
          alternativeLabel
          sx={{ mb: 4 }}
        >
          {questions.map((_, index) => (
            <Step key={index}>
              <StepLabel></StepLabel>
            </Step>
          ))}
        </Stepper>

        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>
            {currentQuestion?.question}
          </Typography>

          {renderAnswerInput()}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!currentAnswer}
            >
              Next
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Interview; 
